// A reusable component for message actions (like, dislike, copy, etc.)
import { FC, useEffect, useState } from "react";
import { Check, Copy, Edit, RotateCcw, RefreshCw, ThumbsDown, ThumbsUp, Eye, EyeOff, Download, Redo2, ClipboardCopy, Square, MessageSquare, CornerUpLeft, MessageCircle, StopCircle } from "lucide-react";
import { t } from "i18next";
import { useTranslation } from 'react-i18next';

interface MessageActionsProps {
  isAssistant: boolean;
  isLast: boolean;
  isEditing: boolean;
  isHovering: boolean;
  onCopy: () => void;
  copyStatus?: 'idle' | 'success' | 'error';
  onEdit?: () => void;
  onReply: () => void;
  onRegenerate?: () => void;
  messageId: string;
  showActions: boolean;
  onLike: () => void;
  onDislike: () => void;
  feedback?: 'like' | 'dislike' | null;
  isStreaming: boolean;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  isAssistant,
  isLast,
  isEditing,
  isHovering,
  onCopy,
  copyStatus = 'idle',
  onEdit,
  onReply,
  onRegenerate,
  messageId,
  showActions,
  onLike,
  onDislike,
  feedback,
  isStreaming,
}) => {
  const { t } = useTranslation();
  const canRegenerate = isAssistant && !isStreaming;
  const canEdit = !isAssistant && onEdit;
  const isVisible = showActions && isHovering && !isStreaming;
  const showFeedbackButtons = isAssistant && onLike && onDislike;

  return (
    <div
      className={`absolute -bottom-3 right-2 flex items-center space-x-1 p-0.5 bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-md shadow-sm transition-opacity duration-150 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {showFeedbackButtons && (
        <>
          <button
            onClick={onLike}
            className={`p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-600 ${
              feedback === 'like' ? 'text-green-500' : 'text-neutral-500 dark:text-neutral-400'
            }`}
            title={feedback === 'like' ? t('remove_like') : t('like_message')}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>

          <button
            onClick={onDislike}
            className={`p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-600 ${
              feedback === 'dislike' ? 'text-red-500' : 'text-neutral-500 dark:text-neutral-400'
            }`}
            title={feedback === 'dislike' ? t('remove_dislike') : t('dislike_message')}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
        </>
      )}

      <button
        onClick={onCopy}
        className={`p-1 rounded ${
          copyStatus === 'success' 
            ? 'text-green-500' 
            : copyStatus === 'error' 
              ? 'text-red-500' 
              : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-600'
        }`}
        title={
          copyStatus === 'success' 
            ? t('copied') 
            : copyStatus === 'error' 
              ? t('copy_failed') 
              : t('copy_message')
        }
        disabled={copyStatus === 'success' || copyStatus === 'error'}
      >
        {copyStatus === 'success' ? (
          <Check className="h-3.5 w-3.5" />
        ) : copyStatus === 'error' ? (
          <Copy className="h-3.5 w-3.5" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>

      <button
        onClick={onReply}
        className="p-1 rounded text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-600"
        title={t('reply_to_message')}
      >
        <CornerUpLeft className="h-3.5 w-3.5" />
      </button>

      {canEdit && (
        <button
          onClick={onEdit}
          className="p-1 rounded text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-600"
          title={t('edit_message')}
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      )}

      {canRegenerate && onRegenerate && (
        <button
          onClick={onRegenerate}
          className="p-1 rounded text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-600"
          title={t('regenerate_response')}
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}; 